import { useCallback, useEffect, useRef, useState } from "react";
import { byId, driverIds, helperIds, opponentIds, rooms } from "./data";
import { cue } from "./audio";
import { CharacterArt } from "./CharacterArt";
import {
  completedMissionObjectives,
  missionForDate,
  missionPlans,
  type HomeSignals,
  type Mission,
} from "./missions";

export type GameProps = {
  finish: (score: number) => void;
  paused: boolean;
  sound: boolean;
  captions: boolean;
  haptics?: boolean;
  missionOverride?: Mission;
};
export type GameModule = {
  id: string;
  title: string;
  subtitle: string;
  color: string;
  minutes: string;
  featuredCharacter: string;
  rules: string[];
  Component: (props: GameProps) => JSX.Element;
};
export const LOCK_SEQUENCE = [
  "聽到 Samsung 關門確認聲",
  "確認鎖舌圖示已上鎖",
  "輕拉門把驗證鎖好",
] as const;
export const HELPER_BENEFITS: Record<
  string,
  { label: string; points: number }
> = {
  bibi: { label: "大佬指令組合分", points: 5 },
  "big-eye": { label: "漏項提示保險", points: 4 },
  fafa: { label: "完美次序分", points: 6 },
  "do-nga": { label: "巡遊速度分", points: 5 },
  chun: { label: "防跌漏保險", points: 4 },
  lamlam: { label: "家庭冷靜回復", points: 5 },
};
export type HelperMechanic = {
  score: number;
  hints: number;
  shield: number;
  calm: number;
  tempo: number;
  multiplier: number;
  ceremony: number;
  mistakes: number;
};
export const emptyHelperMechanic = (): HelperMechanic => ({
  score: 0,
  hints: 0,
  shield: 0,
  calm: 0,
  tempo: 0,
  multiplier: 1,
  ceremony: 0,
  mistakes: 0,
});
export function applyHelperMechanic(
  id: string,
  state: HelperMechanic,
): HelperMechanic {
  switch (id) {
    case "bibi":
      return { ...state, multiplier: 2 };
    case "big-eye":
      return { ...state, hints: state.hints + 1 };
    case "fafa":
      return { ...state, ceremony: state.ceremony + 1, score: state.score + 2 };
    case "do-nga":
      return { ...state, tempo: state.tempo + 1, score: state.score + 1 };
    case "chun":
      return { ...state, shield: state.shield + 1 };
    case "lamlam":
      return {
        ...state,
        calm: state.calm + 1,
        mistakes: Math.max(0, state.mistakes - 1),
      };
    default:
      return state;
  }
}
export function helperMistake(state: HelperMechanic): HelperMechanic {
  return state.shield
    ? { ...state, shield: state.shield - 1 }
    : { ...state, mistakes: state.mistakes + 1, score: state.score - 3 };
}
export function helperCollect(
  state: HelperMechanic,
  inOrder = true,
): HelperMechanic {
  return {
    ...state,
    score:
      state.score +
      state.multiplier +
      (state.ceremony && inOrder ? 2 : 0) +
      (state.tempo ? 1 : 0),
  };
}
export type MissionMechanic = {
  bonus: number;
  requiresRainGear: boolean;
  requiresExceptionCheck: boolean;
  featuredMultiplier: number;
};
export function missionMechanic(mission: Mission): MissionMechanic {
  const plan = missionPlans[mission.id];
  return {
    bonus: plan.bonus,
    requiresRainGear: mission.kind === "rainy",
    requiresExceptionCheck: mission.kind === "lock",
    featuredMultiplier: mission.kind === "combo" ? 2 : 1,
  };
}
export function featuredAbility(id: string, power: number) {
  const role = byId(id).role;
  const ability = /提示|發現|知識|找小物/.test(role)
    ? { flow: "提示下一步", base: 2 }
    : /保住|易碎|救援|安撫/.test(role)
      ? { flow: "保護一次流程", base: 3 }
      : /次序|收納|Checklist|PM|交接/.test(role)
        ? { flow: "核對次序", base: 4 }
        : /速度|節奏|跑跑|極速|路線/.test(role)
          ? { flow: "加快整理", base: 5 }
          : { flow: "穩定家庭", base: 1 };
  return {
    score: power + ability.base,
    flow: ability.flow,
    role,
    token: `${role}-${power}`,
  };
}
export const missionReady = (
  mission: Mission,
  objectives: string[],
  featured: string[],
) =>
  mission.objectives
    .filter(Boolean)
    .every((item) => objectives.includes(item!)) &&
  mission.featured.every((id) => featured.includes(id));
export function haptic(enabled: boolean, pattern: number | number[] = 12) {
  if (
    enabled &&
    typeof navigator !== "undefined" &&
    typeof navigator.vibrate === "function"
  )
    navigator.vibrate(pattern);
}
export const homeReady = (
  helpers: string[],
  helperActions: string[],
  roomDone: Record<string, string[]>,
  ash: "hot" | "cooling" | "cold" | "bagged",
  door: string[],
  lockIndex: number,
  vehicle: string | null,
  requiredDoorCount = 3,
) =>
  helpers.length === 3 &&
  helperActions.length === 3 &&
  rooms.every(
    (room) => (roomDone[room.id] || []).length === room.items.length,
  ) &&
  ash === "bagged" &&
  door.length === requiredDoorCount &&
  lockIndex === LOCK_SEQUENCE.length &&
  !!vehicle;

function Choice({
  id,
  selected,
  onClick,
  disabled,
}: {
  id: string;
  selected: boolean;
  onClick: () => void;
  disabled: boolean;
}) {
  const character = byId(id);
  return (
    <button
      className="choice"
      aria-pressed={selected}
      onClick={onClick}
      disabled={disabled}
    >
      <CharacterArt
        character={character}
        className="mini-portrait"
        decorative
      />
      <b>{character.name}</b>
      <small>{character.role}</small>
    </button>
  );
}

export function HomeGame({
  finish,
  paused,
  sound,
  captions,
  haptics = false,
  missionOverride,
}: GameProps) {
  const mission = missionOverride || missionForDate(),
    missionRules = missionMechanic(mission);
  const [step, setStep] = useState<
    "mission" | "rooms" | "ash" | "door" | "vehicle"
  >("mission");
  const [featuredStages, setFeaturedStages] = useState<
      Record<string, "rooms" | "ash" | "door" | "vehicle">
    >({}),
    [featuredScore, setFeaturedScore] = useState(0),
    [helpers, setHelpers] = useState<string[]>([]),
    [helperActions, setHelperActions] = useState<string[]>([]),
    [helperState, setHelperState] =
      useState<HelperMechanic>(emptyHelperMechanic),
    [room, setRoom] = useState(rooms[0].id),
    [roomDone, setRoomDone] = useState<Record<string, string[]>>({}),
    [itemOrder, setItemOrder] = useState<string[]>([]),
    [shieldSaves, setShieldSaves] = useState(0),
    [calmRecoveries, setCalmRecoveries] = useState(0),
    [routePreviews, setRoutePreviews] = useState<string[]>([]),
    [backupRoute, setBackupRoute] = useState(false),
    [errorsFound, setErrorsFound] = useState(0),
    [lineUp, setLineUp] = useState(false),
    [handover, setHandover] = useState(false),
    [greenHearts, setGreenHearts] = useState(0),
    [wetObstaclesAvoided, setWetObstaclesAvoided] = useState(0),
    [paradeMood, setParadeMood] = useState(0),
    [ash, setAsh] = useState<"hot" | "cooling" | "cold" | "bagged">("hot"),
    [door, setDoor] = useState<string[]>([]),
    [lockIndex, setLockIndex] = useState(0),
    [vehicle, setVehicle] = useState<string | null>(null),
    [event, setEvent] = useState(`婆婆派咗「${mission.title}」。`);
  const toggle = (
    value: string,
    list: string[],
    set: (value: string[]) => void,
    max = 99,
  ) => {
    if (paused) return;
    set(
      list.includes(value)
        ? list.filter((item) => item !== value)
        : list.length < max
          ? [...list, value]
          : list,
    );
    cue(sound);
  };
  const current = rooms.find((item) => item.id === room)!;
  const stageOrder: ("rooms" | "ash" | "door" | "vehicle")[] = [
      "rooms",
      "ash",
      "door",
      "vehicle",
    ],
    currentStage = step === "mission" ? "rooms" : step;
  const signals: HomeSignals = {
    currentStage,
    completedStages: stageOrder.slice(
      0,
      Math.max(0, stageOrder.indexOf(currentStage)),
    ),
    roomsComplete: rooms.every(
      (value) => (roomDone[value.id] || []).length === value.items.length,
    ),
    items: Object.values(roomDone).flat(),
    itemOrder,
    featured: featuredStages,
    ashBagged: ash === "bagged",
    doorChecks: door,
    lockIndex,
    vehicle,
    shieldSaves,
    calmRecoveries,
    calm: helperState.calm,
    routePreviews,
    backupRoute,
    errorsFound,
    lineUp,
    handover,
    greenHearts,
    wetObstaclesAvoided,
    paradeMood,
  };
  const missionObjectives = completedMissionObjectives(mission, signals),
    missionPlan = missionPlans[mission.id];
  const missionDoorChecks = [
      ...(missionRules.requiresRainGear ? ["雨傘已放入防水袋"] : []),
      ...(missionRules.requiresExceptionCheck ? ["電子鎖例外狀態已排除"] : []),
    ],
    requiredDoorCount = 3 + missionDoorChecks.length;
  const stage = {
    mission: "C1 / 6 集合",
    rooms: "C2 / 6 四房",
    ash: "C3 / 6 煙灰",
    door: "C4 / 6 門口",
    vehicle: "C5 / 6 出發",
  }[step];
  const useHelper = (id: string) => {
    if (paused || helperActions.includes(id)) return;
    const benefit = HELPER_BENEFITS[id];
    setHelperActions((value) => [...value, id]);
    setHelperState((value) => applyHelperMechanic(id, value));
    setEvent(`${byId(id).name} 啟動「${benefit.label}」。`);
    cue(sound, 680);
    haptic(haptics);
  };
  const collectRoomItem = (roomId: string, item: string, index: number) => {
    const existing = roomDone[roomId] || [];
    if (paused || existing.includes(item)) return;
    setRoomDone((done) => ({
      ...done,
      [roomId]: [...(done[roomId] || []), item],
    }));
    setItemOrder((value) => [...value, item]);
    setHelperState((value) => helperCollect(value, existing.length === index));
    setEvent(`${item} 已經經正式收集流程收好。`);
    haptic(haptics);
  };
  const featuredAt = (target: "rooms" | "ash" | "door" | "vehicle") =>
    missionPlan.featuredStage === target ? (
      <div className="helper-actions">
        {mission.featured.map((id) => {
          const ability = featuredAbility(id, missionPlan.featuredPower),
            done = featuredStages[id] === target;
          return (
            <button
              key={id}
              className={done ? "done" : ""}
              disabled={paused || done}
              onClick={() => {
                setFeaturedStages((value) => ({ ...value, [id]: target }));
                setFeaturedScore((value) => value + ability.score);
                if (id === "chun")
                  setHelperState((value) => applyHelperMechanic(id, value));
                setEvent(
                  `${byId(id).name}喺 ${target} 章啟動${ability.flow}。`,
                );
              }}
            >
              <CharacterArt character={byId(id)} decorative />
              <span>
                <b>{byId(id).name}</b>
                <small>
                  {done
                    ? "本章能力已生效"
                    : `${ability.flow} · +${ability.score}`}
                </small>
              </span>
            </button>
          );
        })}
      </div>
    ) : null;
  const mechanicsAt = (target: "rooms" | "ash" | "door" | "vehicle") => (
    <div className="mission-mechanics">
      {target === "rooms" && mission.id === "m02" && (
        <button
          disabled={(roomDone.bedroom || []).includes("外套")}
          onClick={() => collectRoomItem("bedroom", "外套", 1)}
        >
          先搬外套大件
        </button>
      )}
      {target === "rooms" && ["m03", "m09"].includes(mission.id) && (
        <button onClick={() => setWetObstaclesAvoided((value) => value + 1)}>
          抹乾並跨過濕滑位置
        </button>
      )}
      {target === "rooms" && mission.id === "m03" && (
        <button
          onClick={() => {
            setHelperState((value) => {
              if (value.shield > 0) {
                setShieldSaves((count) => count + 1);
                return helperMistake(value);
              }
              return helperMistake(value);
            });
          }}
        >
          易碎物跌漏測試
        </button>
      )}
      {target === "rooms" && mission.id === "m05" && (
        <>
          <button
            onClick={() =>
              setHelperState((value) => ({
                ...value,
                mistakes: value.mistakes + 1,
              }))
            }
          >
            觸發成員走失
          </button>
          <button
            disabled={!featuredStages.lamlam || helperState.mistakes === 0}
            onClick={() => {
              setHelperState((value) => ({
                ...applyHelperMechanic("lamlam", value),
                calm: value.calm + 1,
              }));
              setCalmRecoveries((value) => value + 1);
            }}
          >
            攬攬安撫並回復冷靜
          </button>
        </>
      )}
      {target === "rooms" &&
        mission.id === "m07" &&
        ["海濱路線", "公園路線"].map((route) => (
          <button
            key={route}
            disabled={routePreviews.includes(route)}
            onClick={() => setRoutePreviews((value) => [...value, route])}
          >
            預覽{route}
          </button>
        ))}
      {target === "rooms" && mission.id === "m10" && (
        <button onClick={() => setGreenHearts((value) => value + 1)}>
          收集綠心
        </button>
      )}
      {target === "door" && mission.id === "m06" && (
        <button onClick={() => setLineUp(true)}>按身高正式排隊</button>
      )}
      {target === "door" && mission.id === "m07" && (
        <>
          <button onClick={() => setBackupRoute(true)}>準備公園後備路線</button>
          <button onClick={() => setErrorsFound((value) => value + 1)}>
            指出垃圾位置錯誤
          </button>
        </>
      )}
      {target === "door" && mission.id === "m10" && (
        <button onClick={() => setWetObstaclesAvoided((value) => value + 1)}>
          繞過濕滑路障
        </button>
      )}
      {target === "vehicle" && mission.id === "m06" && (
        <button disabled={!vehicle} onClick={() => setHandover(true)}>
          把物品交接上車
        </button>
      )}
      {target === "vehicle" && mission.id === "m10" && (
        <button
          disabled={
            Object.values(featuredStages).filter((stage) => stage === "vehicle")
              .length < 3
          }
          onClick={() => setParadeMood((value) => value + 1)}
        >
          開始巡遊保持開心
        </button>
      )}
    </div>
  );
  return (
    <section className="game-scene home-game" aria-label="出門大作戰">
      <header>
        <div>
          <small>六章家庭任務</small>
          <b>出門大作戰</b>
        </div>
        <span>{stage}</span>
      </header>
      <div className="stage-progress">
        <span
          style={{
            width: `${({ mission: 16, rooms: 33, ash: 50, door: 67, vehicle: 84 } as const)[step]}%`,
          }}
        />
      </div>
      {step === "mission" && (
        <div className="panel">
          <h2>{mission.title}</h2>
          <p>
            {mission.modifier}
            。目標會由真實執房、煙灰、門鎖同揀車行動完成；流程「
            {missionPlan.flow}」值 {missionPlan.bonus} 分。
          </p>
          <h3>今日 2–3 個目標</h3>
          {mission.objectives.filter(Boolean).map((item, index) => (
            <div className="safety mission-objective" key={item}>
              <span className="safety-marker" aria-hidden>○</span>
              <span className="safety-copy">
                {item}
                <small>
                  {({ rooms: "四房執拾", ash: "煙灰安全", door: "門口安全", vehicle: "揀車出發" } as const)[missionPlan.actions[index]!.stage]}階段完成
                </small>
              </span>
            </div>
          ))}
          <h3>今日登場角色</h3>
          <div className="helper-actions">
            {mission.featured.map((id) => {
              const ability = featuredAbility(id, missionPlan.featuredPower);
              return (
                <div className="featured-preview" key={id}>
                  <CharacterArt character={byId(id)} decorative />
                  <span>
                    <b>{byId(id).name}</b>
                    <small>
                      {missionPlan.featuredStage} 章啟動 · {ability.flow}
                    </small>
                  </span>
                </div>
              );
            })}
          </div>
          <h3>揀三位 Helper</h3>
          <div className="choice-grid">
            {helperIds.map((id) => (
              <Choice
                key={id}
                id={id}
                selected={helpers.includes(id)}
                disabled={paused}
                onClick={() => toggle(id, helpers, setHelpers, 3)}
              />
            ))}
          </div>
          <p className="selection-status" aria-live="polite">
            登場能力會喺 {missionPlan.featuredStage} 章啟動 · Helper{" "}
            {helpers.length}/3
          </p>
          <button
            className="primary"
            disabled={helpers.length !== 3 || paused}
            onClick={() => {
              setStep("rooms");
              setEvent(`${mission.title} 正式開始；實際行動先會完成目標。`);
            }}
          >
            接受任務 →
          </button>
        </div>
      )}
      {step === "rooms" && (
        <div className="panel">
          <h2>四房執拾＋Helper 行動</h2>
          <p data-testid="helper-state">
            分數 {helperState.score} · 倍率 ×{helperState.multiplier} · 提示{" "}
            {helperState.hints} · 保護 {helperState.shield} · 冷靜{" "}
            {helperState.calm} · 節奏 {helperState.tempo} · 次序{" "}
            {helperState.ceremony} · 手滑 {helperState.mistakes}
          </p>
          {helperState.hints > 0 && (
            <p className="helper-hint" role="status">
              大眼提示：下一件收好「
              {current.items.find(
                (item) => !(roomDone[current.id] || []).includes(item),
              ) || "本房完成"}
              」
            </p>
          )}
          {featuredAt("rooms")}
          {mechanicsAt("rooms")}
          <div className="helper-actions">
            {helpers.map((id) => (
              <button
                key={id}
                className={helperActions.includes(id) ? "done" : ""}
                disabled={paused || helperActions.includes(id)}
                onClick={() => useHelper(id)}
              >
                <CharacterArt character={byId(id)} decorative />
                <span>
                  <b>{byId(id).name}</b>
                  <small>
                    {helperActions.includes(id)
                      ? "獨特機制已套用"
                      : HELPER_BENEFITS[id].label}
                  </small>
                </span>
              </button>
            ))}
          </div>
          <button
            className="safety mistake"
            onClick={() => {
              setHelperState((value) => {
                if (value.shield > 0) setShieldSaves((count) => count + 1);
                return helperMistake(value);
              });
              setEvent(
                helperState.shield
                  ? "純純接住咗，保護已消耗。"
                  : "手滑跌漏，家庭分數 -3。",
              );
            }}
            disabled={paused}
          >
            模擬手滑／跌漏
          </button>
          <div className="tabs">
            {rooms.map((value) => (
              <button
                key={value.id}
                aria-pressed={room === value.id}
                onClick={() => setRoom(value.id)}
                disabled={paused}
              >
                {value.name}
                <small>
                  {(roomDone[value.id] || []).length}/{value.items.length}
                </small>
              </button>
            ))}
          </div>
          <div className={`room-stage ${current.id}`} aria-label={current.name}>
            {current.items.map((item, index) => (
              <button
                key={item}
                disabled={paused}
                className={
                  (roomDone[current.id] || []).includes(item) ? "collected" : ""
                }
                onClick={() => {
                  collectRoomItem(current.id, item, index);
                }}
              >
                <span aria-hidden>
                  {(roomDone[current.id] || []).includes(item) ? "✓" : "＋"}
                </span>
                {item}
              </button>
            ))}
          </div>
          <button
            className="primary"
            disabled={
              !signals.roomsComplete ||
              helperActions.length !== 3 ||
              (missionPlan.featuredStage === "rooms" &&
                Object.values(featuredStages).filter(
                  (stage) => stage === "rooms",
                ).length < 3) ||
              paused
            }
            onClick={() => {
              setStep("ash");
              setEvent("四間房同三個獨特 Helper 機制完成。");
            }}
          >
            檢查煙灰缸 →
          </button>
        </div>
      )}
      {step === "ash" && (
        <div className="panel ash-stage">
          <h2>煙灰缸安全</h2>
          {featuredAt("ash")}
          {mechanicsAt("ash")}
          <p>
            只有完全熄滅、摸落冰冷嘅煙灰先可以入垃圾袋。未安全時任務會停低，唔扣家庭冷靜。
          </p>
          <div className={`ash-status ${ash}`} aria-live="polite">
            <span aria-hidden>♨</span>
            <b>
              {ash === "hot"
                ? "仍然熱"
                : ash === "cooling"
                  ? "等待完全冷卻"
                  : ash === "cold"
                    ? "已確認冰冷"
                    : "已安全入袋"}
            </b>
          </div>
          {ash === "hot" && (
            <button
              className="primary"
              onClick={() => {
                setAsh("cooling");
                setEvent("安全暫停：等待煙灰完全冷卻。");
              }}
            >
              熄滅並等待
            </button>
          )}
          {ash === "cooling" && (
            <button
              className="primary"
              onClick={() => {
                setAsh("cold");
                setEvent("用手背確認：煙灰已經冰冷。");
              }}
            >
              確認已完全冷卻
            </button>
          )}
          {ash === "cold" && (
            <button
              className="primary"
              onClick={() => {
                setAsh("bagged");
                setEvent("冷煙灰已封好入垃圾袋。");
              }}
            >
              封好垃圾袋
            </button>
          )}
          {ash === "bagged" && (
            <button className="primary" onClick={() => setStep("door")}>
              去門口 →
            </button>
          )}
        </div>
      )}
      {step === "door" && (
        <div className="panel">
          <h2>Samsung 電子鎖安全程序</h2>
          {featuredAt("door")}
          {mechanicsAt("door")}
          <p>先處理人、寵物同垃圾位置，再依次做聲音 → 鎖舌 → 輕拉。</p>
          <div className="door-team">
            <CharacterArt character={byId("bow")} decorative />
            <CharacterArt character={byId("wong-choi")} decorative />
          </div>
          {[
            "點齊全部仔s，確認旺財離開門罅",
            "垃圾袋放門外指定位置",
            "慢慢關門，確認冇夾到任何成員",
            ...missionDoorChecks,
          ].map((item, index) => (
            <button
              className={`safety ${door.includes(item) ? "done" : ""}`}
              key={item}
              disabled={paused}
              onClick={() => toggle(item, door, setDoor)}
            >
              <span>{door.includes(item) ? "✓" : index + 1}</span>
              {item}
            </button>
          ))}
          <h3>驗鎖次序</h3>
          {LOCK_SEQUENCE.map((item, index) => (
            <button
              className={`safety ${index < lockIndex ? "done" : ""}`}
              key={item}
              disabled={
                paused ||
                door.length !== requiredDoorCount ||
                index !== lockIndex
              }
              onClick={() => {
                setLockIndex((value) => value + 1);
                setEvent(`${item}：完成。`);
                cue(sound, 520 + index * 100);
                haptic(haptics);
              }}
            >
              <span>{index < lockIndex ? "✓" : index + 1}</span>
              {item}
            </button>
          ))}
          <button
            className="primary"
            disabled={
              door.length !== requiredDoorCount ||
              lockIndex !== 3 ||
              (missionPlan.featuredStage === "door" &&
                Object.values(featuredStages).filter(
                  (stage) => stage === "door",
                ).length < 3) ||
              paused
            }
            onClick={() => setStep("vehicle")}
          >
            揀車出發 →
          </button>
        </div>
      )}
      {step === "vehicle" && (
        <div className="panel">
          <h2>揀出發拍檔</h2>
          {featuredAt("vehicle")}
          {mechanicsAt("vehicle")}
          <p>一仔溫柔穩定；馬仔速度快、時機較緊。</p>
          <div className="choice-grid vehicles">
            {["car-one", "car-horse"].map((id) => (
              <Choice
                key={id}
                id={id}
                selected={vehicle === id}
                disabled={paused}
                onClick={() => {
                  setVehicle(id);
                  setEvent(`${byId(id).name} 完成揀車目標。`);
                  cue(sound);
                  haptic(haptics);
                }}
              />
            ))}
          </div>
          <h3>
            實際完成目標 {missionObjectives.length}/
            {mission.objectives.filter(Boolean).length}
          </h3>
          {mission.objectives.filter(Boolean).map((item) => (
            <p className="mission-result" key={item}>
              {missionObjectives.includes(item!) ? "✓" : "○"} {item}
            </p>
          ))}
          <aside className="family-ritual">
            <CharacterArt character={byId("dai-lup")} decorative />
            <p>
              <b>大粒：</b>「一家人齊齊整整，出發去探婆婆！」
            </p>
          </aside>
          <button
            className="primary"
            disabled={
              !homeReady(
                helpers,
                helperActions,
                roomDone,
                ash,
                door,
                lockIndex,
                vehicle,
                requiredDoorCount,
              ) ||
              missionObjectives.length !==
                mission.objectives.filter(Boolean).length ||
              Object.keys(featuredStages).length !== mission.featured.length ||
              paused
            }
            onClick={() =>
              finish(
                Math.min(
                  100,
                  (vehicle === "car-horse" ? 60 : 56) +
                    missionRules.bonus +
                    featuredScore +
                    helperState.score +
                    helperState.hints * 3 +
                    helperState.shield * 3 +
                    helperState.calm * 3 +
                    helperState.tempo * 3 -
                    helperState.mistakes * 2,
                ),
              )
            }
          >
            C6 · 安全抵達婆婆屋企
          </button>
        </div>
      )}
      <p className="event-caption" role="status" aria-live="polite">
        {event}
      </p>
      {captions && <p className="caption">聲音字幕：{event}</p>}
    </section>
  );
}

type RunnerObject = { id: number; x: number; kind: "shoe" | "bag" | "heart" };
export function Runner({
  finish,
  paused,
  sound,
  captions,
  haptics = false,
}: GameProps) {
  const [jump, setJump] = useState(false),
    [score, setScore] = useState(0),
    [hearts, setHearts] = useState(3),
    [time, setTime] = useState(25),
    [objects, setObjects] = useState<RunnerObject[]>([]),
    [message, setMessage] = useState("多牙準備好喇！");
  const sequence = useRef(0),
    spawnClock = useRef(0),
    finishing = useRef(false),
    jumpTimer = useRef<ReturnType<typeof setTimeout>>();
  const hop = useCallback(() => {
    if (paused || jump) return;
    setJump(true);
    setMessage("多牙跳起！");
    cue(sound);
    haptic(haptics, 10);
    clearTimeout(jumpTimer.current);
    jumpTimer.current = setTimeout(() => setJump(false), 430);
  }, [paused, jump, sound, haptics]);
  useEffect(() => () => clearTimeout(jumpTimer.current), []);
  useEffect(() => {
    if (paused) return;
    const timer = setInterval(() => {
      spawnClock.current += 50;
      if (spawnClock.current % 1000 === 0) setTime((value) => Math.max(0, value - 1));
      setObjects((value) => {
        const moved = value.map((object) => ({ ...object, x: object.x - 1.25 })).filter((object) => object.x > -12);
        if (spawnClock.current % 1250 !== 0) return moved;
        sequence.current += 1;
        return [...moved, { id: sequence.current, x: 104, kind: sequence.current % 4 === 0 ? "heart" : sequence.current % 2 === 0 ? "shoe" : "bag" }];
      });
    }, 50);
    return () => clearInterval(timer);
  }, [paused]);
  useEffect(() => {
    const near = objects.filter((object) => object.x <= 25 && object.x >= 12);
    if (!near.length) return;
    setObjects((value) =>
      value.filter((object) => !near.some((hit) => hit.id === object.id)),
    );
    near.forEach((object) => {
      if (object.kind === "heart") {
        setScore((value) => value + 12);
        setMessage("收到大粒嘅愛心！");
        cue(sound, 740);
        haptic(haptics, 8);
      } else if (!jump) {
        setHearts((value) => value - 1);
        setMessage("撞到喇，攬攬幫多牙企返起身！");
        cue(sound, 180);
        haptic(haptics, [20, 30, 20]);
      }
    });
  }, [objects, jump, sound, haptics]);
  useEffect(() => {
    if (!finishing.current && (time === 0 || hearts <= 0)) {
      finishing.current = true;
      finish(Math.max(20, score + Math.max(0, hearts) * 18));
    }
  }, [time, hearts, score, finish]);
  useEffect(() => {
    const key = (event: KeyboardEvent) => {
      if (event.code === "Space" || event.key === "ArrowUp") {
        event.preventDefault();
        hop();
      }
    };
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  }, [hop]);
  return (
    <section className="game-scene runner" aria-label="多牙跑跑大冒險">
      <header>
        <div>
          <small>花粉居 → 婆婆屋企</small>
          <b>多牙跑跑</b>
        </div>
        <span>{time}s</span>
      </header>
      <div className="runner-stats">
        <span aria-label={`家庭冷靜 ${hearts}`}>
          {"♥".repeat(Math.max(0, hearts))}
        </span>
        <b>{score} 分</b>
      </div>
      <button
        className="runner-field"
        onPointerDown={hop}
        disabled={paused}
        aria-label="撳一下令多牙跳起"
      >
        <div className="cloud one" />
        <div className="cloud two" />
        <CharacterArt
          character={byId("do-nga")}
          className={`runner-character ${jump ? "jump" : ""}`}
          decorative
        />
        {objects.map((object) => (
          <span
            className={`moving-object ${object.kind}`}
            style={{ left: `${object.x}%` }}
            key={object.id}
            aria-hidden
          >
            {object.kind === "heart" ? "♥" : object.kind === "shoe" ? "⌁" : "▰"}
          </span>
        ))}
        <div className="road-lines" />
      </button>
      <button className="jump-button" onClick={hop} disabled={paused}>
        跳！<small>Tap / Space / ↑</small>
      </button>
      <p className="game-message" role="status" aria-live="polite">
        {message}
      </p>
      {captions && <p className="caption">聲音字幕：{message}</p>}
    </section>
  );
}

export const opponentProgress = (distance: number) =>
  [8, 2, -5, -11].map((offset, index) => Math.max(0, Math.min(100, distance + offset + ((Math.floor(distance / 12) + index) % 3 - 1) * 2)));
export const rankFromProgress = (distance: number, opponents = opponentProgress(distance)) =>
  1 + opponents.filter((progress) => progress > distance).length;
export const rankFor = (distance: number, _hits = 0) => rankFromProgress(distance);
export const nextObstacle = (distance: number) => {
  const id = Math.floor(distance / 20) + 1;
  return { id, lane: id % 3, at: id * 20, phase: Math.max(0, Math.min(1, 1 - (id * 20 - distance) / 20)) };
};
export const racingScore = (hits: number, boostsUsed: number) =>
  Math.max(35, 95 - hits * 12 + Math.min(5, boostsUsed * 2));
export type RacingTuning = {
  speed: number;
  collisionPenalty: number;
  boostDistance: number;
  scoreBonus: number;
  preview: boolean;
};
export function racingTuning(driver: string, vehicle: string): RacingTuning {
  const tuning: RacingTuning = {
    speed: vehicle === "car-horse" ? 5 : 3,
    collisionPenalty: vehicle === "car-one" ? 6 : 9,
    boostDistance: vehicle === "car-horse" ? 11 : 9,
    scoreBonus: vehicle === "car-horse" ? 4 : 1,
    preview: false,
  };
  switch (driver) {
    case "dai-lup":
      tuning.collisionPenalty -= 2;
      break;
    case "dai-fa":
      tuning.boostDistance += 3;
      break;
    case "bibi":
      tuning.scoreBonus += 5;
      break;
    case "big-eye":
      tuning.preview = true;
      break;
    case "fafa":
      tuning.collisionPenalty -= 1;
      tuning.scoreBonus += 2;
      break;
    case "do-nga":
      tuning.speed += 1;
      break;
  }
  return tuning;
}
export function Racing({
  finish,
  paused,
  sound,
  captions,
  haptics = false,
}: GameProps) {
  const [driver, setDriver] = useState<string | null>(null),
    [vehicle, setVehicle] = useState<string | null>(null),
    [lane, setLane] = useState(1),
    [distance, setDistance] = useState(0),
    [hits, setHits] = useState(0),
    [boosts, setBoosts] = useState(3),
    [message, setMessage] = useState("車手準備中。");
  const lastObstacle = useRef(-1);
  const ready = !!driver && !!vehicle,
    tuning = ready ? racingTuning(driver, vehicle) : null,
    speed = tuning?.speed || 3,
    obstacle = nextObstacle(distance),
    obstacleLane = obstacle.lane,
    obstaclePhase = obstacle.phase,
    opponents = opponentProgress(distance),
    rank = rankFromProgress(distance, opponents);
  const advance = useCallback((amount: number) => {
    setDistance((currentDistance) => {
      const next = Math.min(100, currentDistance + amount);
      const event = nextObstacle(currentDistance);
      const crossedEvent = next >= event.at && event.id > lastObstacle.current;
      if (crossedEvent && lane === event.lane) {
        lastObstacle.current = event.id;
        setHits((value) => value + 1);
        setMessage("碰到路障，攬攬幫車隊回復冷靜。");
        cue(sound, 160);
        haptic(haptics, [20, 30, 20]);
        return Math.max(0, next - (tuning?.collisionPenalty || 8));
      }
      if (crossedEvent) lastObstacle.current = event.id;
      return next;
    });
  }, [haptics, lane, sound, tuning?.collisionPenalty]);
  useEffect(() => {
    if (!ready || paused) return;
    const timer = setInterval(() => advance(speed), 350);
    return () => clearInterval(timer);
  }, [ready, paused, speed, advance]);
  useEffect(() => {
    if (distance >= 100)
      finish(
        Math.min(
          100,
          racingScore(hits, 3 - boosts) + (tuning?.scoreBonus || 0),
        ),
      );
  }, [distance, hits, boosts, finish, tuning?.scoreBonus]);
  const move = useCallback(
    (amount: number) => {
      if (paused) return;
      setLane((value) => Math.max(0, Math.min(2, value + amount)));
      cue(sound);
    },
    [paused, sound],
  );
  useEffect(() => {
    const key = (event: KeyboardEvent) => {
      if (event.key === "ArrowLeft") move(-1);
      if (event.key === "ArrowRight") move(1);
    };
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  }, [move]);
  if (!ready)
    return (
      <section className="game-scene selection">
        <header>
          <div>
            <small>花粉居仔s極速盃</small>
            <b>組成車隊</b>
          </div>
          <span>{driver ? "2" : "1"} / 2</span>
        </header>
        <div className="panel">
          <h2>1. 揀車手</h2>
          <p>每位家庭成員都會親自落場。</p>
          <div className="choice-grid drivers">
            {driverIds.map((id) => (
              <Choice
                key={id}
                id={id}
                selected={driver === id}
                disabled={paused}
                onClick={() => setDriver(id)}
              />
            ))}
          </div>
          <h2>2. 揀車輛</h2>
          <div className="choice-grid vehicles">
            {["car-one", "car-horse"].map((id) => (
              <Choice
                key={id}
                id={id}
                selected={vehicle === id}
                disabled={paused}
                onClick={() => setVehicle(id)}
              />
            ))}
          </div>
        </div>
      </section>
    );
  return (
    <section className="game-scene racing">
      <header>
        <div>
          <small>
            {byId(driver).name} × {byId(vehicle).name}
          </small>
          <b>海濱賽道</b>
        </div>
        <span>第 {rank} / 5 位</span>
      </header>
      <div className="race-progress">
        <span style={{ width: `${distance}%` }} />
        <b>{distance}%</b>
      </div>
      {tuning?.preview && (
        <p className="obstacle-preview" role="status">
          大眼預告：路障喺第 {obstacleLane + 1} 線
        </p>
      )}
      <div className="track" aria-label="三線海濱賽道">
        <div className="water">花粉居海濱</div>
        {opponentIds.map((id, index) => (
          <div
            className="canonical-opponent"
            style={{
              left: `calc(${(index % 3) * 33.333}% + 5%)`,
              bottom: `${28 + Math.max(-12, Math.min(40, opponents[index] - distance)) * 1.15}%`,
            }}
            key={id}
          >
            <CharacterArt character={byId(id)} decorative />
            <small>{byId(id).name}</small>
          </div>
        ))}
        <span
          className="race-obstacle"
          style={{ left: `calc(${obstacleLane * 33.333}% + 13%)`, bottom: `${82 - obstaclePhase * 58}%` }}
          aria-hidden
        >
          ◆
        </span>
        <div className="kart" style={{ left: `calc(${lane * 33.3}% + 7%)` }}>
          <CharacterArt character={byId(vehicle)} decorative />
          <small>{byId(driver).name}</small>
        </div>
      </div>
      <div className="race-controls">
        <button
          onClick={() => move(-1)}
          disabled={paused}
          aria-label="向左轉線"
        >
          ←
        </button>
        <button
          className="boost"
          onClick={() => {
            if (!paused && boosts) {
              advance(tuning?.boostDistance || 10);
              setBoosts((value) => value - 1);
              setMessage("抱抱加速成功，分數只會增加！");
              cue(sound, 800);
              haptic(haptics, 18);
            }
          }}
          disabled={paused || !boosts}
        >
          抱抱加速 <small>{boosts} 次</small>
        </button>
        <button onClick={() => move(1)} disabled={paused} aria-label="向右轉線">
          →
        </button>
      </div>
      <p className="game-message" role="status" aria-live="polite">
        {message}
      </p>
      {captions && <p className="caption">聲音字幕：{message}</p>}
    </section>
  );
}

export const gameModules: GameModule[] = [
  {
    id: "home",
    title: "出門大作戰",
    subtitle: "全家執齊物品、鎖好門去探婆婆",
    color: "#408760",
    minutes: "3–5 分鐘",
    featuredCharacter: "dai-lup",
    rules: [
      "揀三位仔加入 Helper Team",
      "執齊四間房，再完成門口安全檢查",
      "選一仔或馬仔，全家安全出發",
    ],
    Component: HomeGame,
  },
  {
    id: "runner",
    title: "多牙跑跑大冒險",
    subtitle: "跳過日常障礙，收集大粒嘅愛心",
    color: "#277cad",
    minutes: "1–3 分鐘",
    featuredCharacter: "do-nga",
    rules: [
      "Tap、Space 或 ↑ 令多牙跳起",
      "避開拖鞋同垃圾袋，收集愛心",
      "碰撞後由攬攬扶起，繼續向前",
    ],
    Component: Runner,
  },
  {
    id: "racing",
    title: "仔s極速盃",
    subtitle: "揀車手配一仔或馬仔，海濱見",
    color: "#d26b39",
    minutes: "2–4 分鐘",
    featuredCharacter: "car-one",
    rules: [
      "分開揀家庭車手同出戰車輛",
      "觸控或 ← → 轉線避開路障",
      "用抱抱加速追過四位 canonical 對手",
    ],
    Component: Racing,
  },
];
